import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, Select, Flex, message, Popconfirm, Modal, Input, Typography } from 'antd';
import dayjs from 'dayjs';
import { useBatchJobsActionHook } from './hooks/useBatchJobsActionHook';
import { Link } from 'react-router-dom';
import {
	HomeTabKeyEnum,
} from "../../components/utils";
import {
  MIN_STRATEGIES_FOR_SF_UPLOAD,
  countStrategiesInJobResult,
  isSfUploadAvailable,
} from "./utils/sfUploadAvailability";

// A job in RUNNING/PENDING/SUBMITTED for longer than this is assumed stuck
// and can be deleted from the listing.
const STUCK_THRESHOLD_HOURS = 6;

const isJobStuck = (jobStatus: string, jobCreatedAt: string | null | undefined): boolean => {
  if (!jobCreatedAt) return false;
  const stuckStatuses = ['RUNNING', 'PENDING', 'SUBMITTED'];
  if (!stuckStatuses.includes(jobStatus)) return false;
  return dayjs().diff(dayjs(jobCreatedAt), 'hour') >= STUCK_THRESHOLD_HOURS;
};

interface BatchJobActionComponentProps {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  record: any;
  onRefresh?: () => Promise<void>;
}

const BatchJobActionComponent: React.FC<BatchJobActionComponentProps> = ({ setIsLoading, record, onRefresh }) => {
  const { handleUploadSFClick, handleGenerateSyndicationSheet, handleUpdateExistingSheet, handleResubmitJob, handleDeleteBatchJob } = useBatchJobsActionHook(setIsLoading, onRefresh);
  const [updateSheetModalVisible, setUpdateSheetModalVisible] = useState<boolean>(false);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState<string>('');
  const [ sfUploadDisabled, setSFUploadDisabled ] = useState<boolean>(true);
  const [ sfUploadProgramErrors, setSFUploadProgramErrors ] = useState<string[]>([]);
  const [planOptions, setPlanOptions] = useState<{ value: string; label: string }[]>([]);
  const [programOptions, setProgramOptions] = useState<{ value: string; label: string }[]>([]);
  const [programVersionOptions, setProgramVersionOptions] = useState<{ value: string; label: string }[]>([
    { value: "V1", label: "V1" },
    { value: "V2", label: "V2" },
    { value: "V3", label: "V3" },
    { value: "V4", label: "V4" },
    { value: "V5", label: "V5" },
    { value: "V6", label: "V6" }
  ]);

  useEffect(() => {
    // Strategy (plan) options come from job_result_json, which the syndication
    // autopodder writes incrementally after each strategy finishes (status stays
    // RUNNING). Parse it independently so syndication actions can appear mid-run.
    if(record.job_result_json !== "" && record.job_result_json !== null) {
      try{
        const json_result = JSON.parse(record.job_result_json);
        // For broadcast jobs, strategies are in 'data', for syndication jobs they're in 'strategies'
        const strategiesObj = json_result?.data || json_result?.strategies;
        if (strategiesObj && typeof strategiesObj === 'object') {
          setPlanOptions(Object.keys(strategiesObj).map((key) => {
            return { value: key, label: key };
          }));
        }
      } catch(e) {
        console.error(e);
      }
    }

    // Program options / SF-upload errors come from processing_info_json, which
    // the broadcast autopodder writes once the structure file is loaded, before
    // any strategy runs. Used only by the broadcast path, so parse it
    // independently and don't let it gate the syndication path.
    if(record.processing_info_json !== "" && record.processing_info_json !== null) {
      let _programOptions: { value: string; label: string }[] = [];
      try{
        const json_processing_info = JSON.parse(record.processing_info_json);
        if (json_processing_info && Array.isArray(json_processing_info.programs)) {
          _programOptions = json_processing_info.programs.map((key: string) => {
            return { value: key, label: key };
          });
          setProgramOptions(_programOptions);
        }
        if (json_processing_info && json_processing_info.errors && json_processing_info.errors['programs'] && Array.isArray(json_processing_info.errors['programs']) && json_processing_info.errors['programs'].length > 0) {
          setSFUploadProgramErrors(json_processing_info.errors['programs']);
        }
      } catch(e) {
        console.error(e);
      }

      setSFUploadDisabled(_programOptions.length != 1);
    }
  }, [record]);
  

  const jobStatus = record.job_status?.status || record.job_status;
  const isSyndication = record.autopoddermode === 'syndication';
  const canResubmit = isSyndication && (jobStatus === 'FINISHED' || jobStatus === 'FAILED');
  const canDelete = jobStatus === 'FAILED' || isJobStuck(jobStatus, record.job_created_at);

  // Count strategies actually present in job_result_json. A freshly submitted job
  // may carry a placeholder (e.g. "{}") rather than ""/null, so an emptiness check
  // alone isn't enough — only treat results as "landed" once a strategy exists.
  const strategyCount = countStrategiesInJobResult(record.job_result_json);

  // No results to act on yet. For syndication this also covers RUNNING jobs whose
  // first strategy hasn't been saved (job_result_json still a placeholder).
  if(record.job_result_json === "" || record.job_result_json === null
     || (isSyndication && strategyCount === 0)) {
    return <Flex vertical={true} gap={8}>
      {canResubmit && (
        <Popconfirm
          title="Resubmit this job?"
          description="This will create a new batch job with the same parameters."
          onConfirm={() => handleResubmitJob(record.id)}
          okText="Resubmit"
          cancelText="Cancel"
        >
          <Button type="default">Resubmit Job</Button>
        </Popconfirm>
      )}
      {canDelete && (
        <Popconfirm
          title="Delete this job?"
          description={jobStatus === 'FAILED'
            ? "This will permanently delete this batch job."
            : `This job has been ${jobStatus} for >${STUCK_THRESHOLD_HOURS}h and appears stuck. Deleting removes the DB record; the underlying AWS Batch job (if still alive) will not be killed.`}
          onConfirm={() => handleDeleteBatchJob(record.id)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Button type="default" danger>Delete Job</Button>
        </Popconfirm>
      )}
    </Flex>;
  }
  // Syndication doesn't use processing_info_json (broadcast-only). Only gate the
  // broadcast path on it: without it there is no program list for the SF upload.
  if(!isSyndication && (record.processing_info_json === "" || record.processing_info_json === null)) {
    return <Flex vertical={true} gap={8}>
      {canResubmit && (
        <Popconfirm
          title="Resubmit this job?"
          description="This will create a new batch job with the same parameters."
          onConfirm={() => handleResubmitJob(record.id)}
          okText="Resubmit"
          cancelText="Cancel"
        >
          <Button type="default">Resubmit Job</Button>
        </Popconfirm>
      )}
      {canDelete && (
        <Popconfirm
          title="Delete this job?"
          description={jobStatus === 'FAILED'
            ? "This will permanently delete this batch job."
            : `This job has been ${jobStatus} for >${STUCK_THRESHOLD_HOURS}h and appears stuck. Deleting removes the DB record; the underlying AWS Batch job (if still alive) will not be killed.`}
          onConfirm={() => handleDeleteBatchJob(record.id)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Button type="default" danger>Delete Job</Button>
        </Popconfirm>
      )}
    </Flex>;
  }

  if(isSyndication) {
    return <Flex vertical={true} gap={8}>
      {canResubmit && (
        <Popconfirm
          title="Resubmit this job?"
          description="This will create a new batch job with the same parameters."
          onConfirm={() => handleResubmitJob(record.id)}
          okText="Resubmit"
          cancelText="Cancel"
        >
          <Button type="default">Resubmit Job</Button>
        </Popconfirm>
      )}
      <Link
        to={{
          pathname: HomeTabKeyEnum.BATCH_JOBS_PODDED_REVIEW,
          search: `?batchJobId=${record.id}`,
        }}
        style={{
          display: 'inline-block',
          padding: '10px 20px',
          backgroundColor: '#1890ff',
          color: '#fff',
          borderRadius: '4px',
          textDecoration: 'none',
          textAlign: 'center',
        }}
      >
        Review Podded Result
      </Link>
      <Link
        to={{
          pathname: HomeTabKeyEnum.BATCH_JOBS_CALENDAR,
          search: `?batchJobId=${record.id}`,
        }}
        style={{
          display: 'inline-block',
          padding: '10px 20px',
          backgroundColor: '#47cb38',
          color: '#fff',
          borderRadius: '4px',
          textDecoration: 'none',
          textAlign: 'center',
        }}
      >
        Calendar View
      </Link>
      <Select
        placeholder="Select strategy"
        options={planOptions}
        style={{ width: '100%' }}
        onChange={(value) => {
          const buttonGenSheet = document.getElementById(`buttonGenSheet_${record.id}`);
          if(buttonGenSheet) {
            buttonGenSheet.setAttribute("data-strategy-name", value);
          }
        }}
      />
      <Button
        type="primary"
        id={`buttonGenSheet_${record.id}`}
        onClick={(element) => {
          const strategyName = element.currentTarget.getAttribute("data-strategy-name");
          if(strategyName) {
            handleGenerateSyndicationSheet(record.id, strategyName);
          } else {
            message.error("Please select a strategy");
          }
        }}
      >
        Generate Syndication Sheet
      </Button>
      <Button
        type="default"
        onClick={() => {
          const buttonGenSheet = document.getElementById(`buttonGenSheet_${record.id}`);
          const strategyName = buttonGenSheet?.getAttribute("data-strategy-name");
          if (!strategyName) {
            message.error("Please select a strategy first");
            return;
          }
          setUpdateSheetModalVisible(true);
        }}
      >
        Update Existing Sheet
      </Button>
      <Modal
        title="Update Existing Post Sheet"
        open={updateSheetModalVisible}
        onOk={() => {
          if (!spreadsheetUrl.trim()) {
            message.error("Please enter a spreadsheet URL or ID");
            return;
          }
          const buttonGenSheet = document.getElementById(`buttonGenSheet_${record.id}`);
          const strategyName = buttonGenSheet?.getAttribute("data-strategy-name");
          if (!strategyName) {
            message.error("Please select a strategy first");
            return;
          }
          // Extract spreadsheet ID from URL or use as-is
          const urlMatch = spreadsheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
          const extractedId = urlMatch ? urlMatch[1] : spreadsheetUrl.trim();
          handleUpdateExistingSheet(record.id, strategyName, extractedId);
          setUpdateSheetModalVisible(false);
          setSpreadsheetUrl('');
        }}
        onCancel={() => {
          setUpdateSheetModalVisible(false);
          setSpreadsheetUrl('');
        }}
        okText="Update Sheet"
      >
        <p>Enter the Google Spreadsheet URL or ID containing the Post sheet to update:</p>
        <Input
          placeholder="https://docs.google.com/spreadsheets/d/... or spreadsheet ID"
          value={spreadsheetUrl}
          onChange={(e) => setSpreadsheetUrl(e.target.value)}
        />
      </Modal>
      {canDelete && (
        <Popconfirm
          title="Delete this job?"
          description={jobStatus === 'FAILED'
            ? "This will permanently delete this batch job."
            : `This job has been ${jobStatus} for >${STUCK_THRESHOLD_HOURS}h and appears stuck. Deleting removes the DB record; the underlying AWS Batch job (if still alive) will not be killed.`}
          onConfirm={() => handleDeleteBatchJob(record.id)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Button type="default" danger>Delete Job</Button>
        </Popconfirm>
      )}
    </Flex>;
  } else {
    // Broadcast: the autopodder writes each strategy's summary and SF csv into
    // job_result_json as the strategy finishes, so the upload can be offered
    // mid-run once the first strategies have landed. The calendar reads the
    // results table, which broadcast only populates when the job finishes.
    const sfUploadAvailable = isSfUploadAvailable(jobStatus, strategyCount);
    return (
      <Flex vertical={true} gap={8}>
        {jobStatus === 'FINISHED' && (
          <Link
            to={{
              pathname: HomeTabKeyEnum.BATCH_JOBS_CALENDAR,
              search: `?batchJobId=${record.id}`,
            }}
            style={{
              display: 'inline-block',
              padding: '10px 20px',
              backgroundColor: '#47cb38',
              color: '#fff',
              borderRadius: '4px',
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            Calendar View
          </Link>
        )}
        {!sfUploadAvailable && jobStatus !== 'FAILED' && (
          <Typography.Text type="secondary">
            SF Upload opens once the first {MIN_STRATEGIES_FOR_SF_UPLOAD} strategies finish
            ({strategyCount}/{MIN_STRATEGIES_FOR_SF_UPLOAD} so far)
          </Typography.Text>
        )}
        {sfUploadAvailable && (
          <>
            <Select placeholder="Program Version" options={programVersionOptions} onChange={(value) => {
              const buttonSFUpload = document.getElementById(`buttonSFUpload_${record.id}`);
              if(buttonSFUpload) {
                buttonSFUpload.setAttribute("data-program-version", value);
              }
            }} />
            <Select placeholder="Select a plan" options={planOptions} onChange={(value) => {
              const buttonSFUpload = document.getElementById(`buttonSFUpload_${record.id}`);
              if(buttonSFUpload) {
                buttonSFUpload.setAttribute("data-strategy-name", value);
              }
            }} />
            <Select placeholder="Select program" options={programOptions} onChange={(value) => {
              const buttonSFUpload = document.getElementById(`buttonSFUpload_${record.id}`);
              if(buttonSFUpload) {
                buttonSFUpload.setAttribute("data-program-name", value);
              }
            }} />
            <Button type="primary" id={`buttonSFUpload_${record.id}`} onClick={(element) => {
              const strategyName = element.currentTarget.getAttribute("data-strategy-name");
              const programName = element.currentTarget.getAttribute("data-program-name");
              const programVersion = element.currentTarget.getAttribute("data-program-version");
              if(strategyName && programName && programVersion) {
                setIsLoading(true);
                handleUploadSFClick({ programVersion, strategyName, programName, batchJobId: record.id });
              } else {
                message.error("Please select a plan and a program");
              }
            }} disabled={sfUploadDisabled}>SF Upload</Button>
            {sfUploadProgramErrors.length > 0 && <Flex vertical={true}>
              {sfUploadProgramErrors.map((error: string) => {
                return <p style={{ color: 'red' }} key={error}>Error: {error}</p>;
              })}
            </Flex>}
          </>
        )}
        {canDelete && (
          <Popconfirm
            title="Delete this job?"
            description={jobStatus === 'FAILED'
              ? "This will permanently delete this batch job."
              : `This job has been ${jobStatus} for >${STUCK_THRESHOLD_HOURS}h and appears stuck. Deleting removes the DB record; the underlying AWS Batch job (if still alive) will not be killed.`}
            onConfirm={() => handleDeleteBatchJob(record.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button type="default" danger>Delete Job</Button>
          </Popconfirm>
        )}
      </Flex>
    );
  }
};

export default BatchJobActionComponent;
